import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiTrash2, FiPlus, FiUsers, FiBook, FiBarChart2, FiClock } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { commonToast } from '../../../helper/Toast';

export default function AdminClassDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState('overview');

    // Sample data - replace with actual API calls
    const classData = {
        id: id,
        name: "Mathematics 101",
        description: "Introduction to Algebra and Geometry",
        grade: 9,
        subject: "Mathematics",
        status: "active",
        teacher: {
            id: 1,
            name: "John Smith",
            email: "john.smith@example.com"
        },
        students: [
            { id: 1, name: "Emma Johnson", progress: 85, lastActivity: "2024-03-15" },
            { id: 2, name: "Michael Brown", progress: 72, lastActivity: "2024-03-14" },
            { id: 3, name: "Sarah Wilson", progress: 90, lastActivity: "2024-03-15" },
            { id: 4, name: "David Lee", progress: 65, lastActivity: "2024-03-13" },
            { id: 5, name: "Lisa Anderson", progress: 78, lastActivity: "2024-03-14" }
        ],
        decks: [
            { id: 1, name: "Basic Algebra", cardCount: 25, completionRate: 80 },
            { id: 2, name: "Linear Equations", cardCount: 30, completionRate: 65 },
            { id: 3, name: "Quadratic Functions", cardCount: 20, completionRate: 45 }
        ],
        statistics: {
            averageProgress: 78,
            completionRate: 75,
            totalStudents: 5,
            activeStudents: 4,
            totalDecks: 3,
            averageScore: 82
        }
    };

    const handleEditClass = () => {
        // Implement edit functionality
        commonToast(t('class_management.edit_success'), 'success');
    };

    const handleDeleteClass = () => {
        // Implement delete functionality
        commonToast(t('class_management.delete_success', "success"));
    };

    const tabs = [
        { id: 'overview', label: t('tabs.overview'), icon: <FiBarChart2 /> },
        { id: 'students', label: t('student'), icon: <FiUsers /> },
        { id: 'decks', label: t('deck'), icon: <FiBook /> },
        { id: 'activity', label: t('activity'), icon: <FiClock /> }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex flex-wrap justify-between items-start mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{classData.name}</h1>
                        <p className="text-gray-600 mt-1">{classData.description}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-4">
                            <span className="text-sm text-gray-500">
                                {t('class_management.class_grade')}: {classData.grade}st Grade
                            </span>
                            <span className="text-sm text-gray-500">
                                {t('class_management.class_subject')}: {classData.subject}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${classData.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                {t(`class_management.${classData.status}`)}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleEditClass}
                            className="p-2 text-blue-600 hover:text-blue-800 rounded-lg hover:bg-blue-50"
                        >
                            <FiEdit2 size={20} />
                        </button>
                        <button
                            onClick={handleDeleteClass}
                            className="p-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50"
                        >
                            <FiTrash2 size={20} />
                        </button>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid md:grid-cols-1 grid-cols-4 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-sm text-blue-600">{t('class_management.total_students')}</div>
                        <div className="text-2xl font-bold text-blue-700">{classData.statistics.totalStudents}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-sm text-green-600">{t('class_management.average_performance')}</div>
                        <div className="text-2xl font-bold text-green-700">{classData.statistics.averageProgress}%</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-sm text-purple-600">{t('class_management.completion_rate')}</div>
                        <div className="text-2xl font-bold text-purple-700">{classData.statistics.completionRate}%</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-sm text-yellow-600">{t('deck')}</div>
                        <div className="text-2xl font-bold text-yellow-700">{classData.statistics.totalDecks}</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px overflow-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium ${activeTab === tab.id
                                    ? 'border-b-2 border-blue-500 text-blue-600'
                                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('class_management.class_statistics')}</h3>
                                <div className="grid md:grid-cols-1 grid-cols-2 gap-6">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">{t('class_management.average_performance')}</h4>
                                        <div className="h-4 bg-gray-200 rounded-full">
                                            <div
                                                className="h-4 bg-blue-500 rounded-full"
                                                style={{ width: `${classData.statistics.averageProgress}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-2xl font-bold text-gray-900 mt-2">
                                            {classData.statistics.averageProgress}%
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">{t('class_management.completion_rate')}</h4>
                                        <div className="h-4 bg-gray-200 rounded-full">
                                            <div
                                                className="h-4 bg-green-500 rounded-full"
                                                style={{ width: `${classData.statistics.completionRate}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-2xl font-bold text-gray-900 mt-2">
                                            {classData.statistics.completionRate}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'students' && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">{t('class_management.class_students')}</h3>
                                <button className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                                    <FiPlus /> {t('add')}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('name')}
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('class_management.average_performance')}
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('class_management.last_activity')}
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {classData.students.map((student) => (
                                            <tr key={student.id}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{student.name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="w-24 h-2 bg-gray-200 rounded-full mr-2">
                                                            <div
                                                                className="h-2 bg-blue-500 rounded-full"
                                                                style={{ width: `${student.progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm text-gray-900">{student.progress}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {student.lastActivity}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button className="text-red-600 hover:text-red-900">
                                                        {t('remove')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'decks' && (
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">{t('deck')}</h3>
                                <button className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                                    <FiPlus /> {t('add')}
                                </button>
                            </div>
                            <div className="grid md:!grid-cols-1 lg:grid-cols-2 grid-cols-3 gap-4">
                                {classData.decks.map((deck) => (
                                    <div key={deck.id} className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="text-lg font-medium text-gray-900">{deck.name}</h4>
                                        <div className="mt-2">
                                            <div className="flex justify-between text-sm text-gray-500">
                                                <span>{t('cards')}: {deck.cardCount}</span>
                                                <span>{deck.completionRate}% {t('complete')}</span>
                                            </div>
                                            <div className="h-2 bg-gray-200 rounded-full mt-2">
                                                <div
                                                    className="h-2 bg-blue-500 rounded-full"
                                                    style={{ width: `${deck.completionRate}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('class_management.last_activity')}</h3>
                            <div className="space-y-4">
                                {classData.students.map((student) => (
                                    <div key={student.id} className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">{student.name}</span>
                                            <span className="text-sm text-gray-500">{student.lastActivity}</span>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600">
                                            {t('progress')}: {student.progress}%
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
